import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '../environments/enviroment';

interface StoredTracking {
  session_id?: string;
  landing_url?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  captured_at?: string;
}

const trackingStorageKey = 'medclass_utm_attribution';
const sessionStorageKey = 'medclass_tracking_session_id';
const trackingTtlMs = 7 * 24 * 60 * 60 * 1000;

export const trackingHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl) ||
      new URLSearchParams(window.location.search).has('publicToken') ||
      req.url.startsWith(`${environment.apiBaseUrl}/public/contenido/`) ||
      req.headers.has('X-Public-Access')) {
    return next(req);
  }

  const tracking = getStoredTracking();

  if (!tracking) {
    return next(req);
  }

  const trackingHeaders = compactHeaders({
    'x-session-id': tracking.session_id || getTrackingSessionId(),
    'x-landing-url': sanitizeTrackingUrl(tracking.landing_url),
    'x-referrer': sanitizeTrackingUrl(tracking.referrer),
    'x-utm-source': tracking.utm_source,
    'x-utm-medium': tracking.utm_medium,
    'x-utm-campaign': tracking.utm_campaign,
    'x-utm-term': tracking.utm_term,
    'x-utm-content': tracking.utm_content,
    'x-gclid': tracking.gclid,
    'x-fbclid': tracking.fbclid,
    'x-msclkid': tracking.msclkid,
    'x-ttclid': tracking.ttclid,
  });

  return next(req.clone({ setHeaders: trackingHeaders }));
};

function getStoredTracking(): StoredTracking | null {
  const rawTracking = localStorage.getItem(trackingStorageKey);

  if (!rawTracking) {
    return null;
  }

  try {
    const tracking = JSON.parse(rawTracking);
    const capturedAt = tracking?.captured_at ? Date.parse(tracking.captured_at) : NaN;

    if (!capturedAt || Date.now() - capturedAt > trackingTtlMs) {
      localStorage.removeItem(trackingStorageKey);
      return null;
    }

    return tracking;
  } catch {
    localStorage.removeItem(trackingStorageKey);
    return null;
  }
}

function sanitizeTrackingUrl(value?: string): string {
  if (!value) return '';

  try {
    const url = new URL(value);
    url.search = '';
    url.hash = '';

    return url.href;
  } catch {
    return '';
  }
}

function compactHeaders(headers: Record<string, string | undefined>) {
  return Object.entries(headers).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value) {
      acc[key] = value;
    }

    return acc;
  }, {});
}

function getTrackingSessionId() {
  const storedSessionId = sessionStorage.getItem(sessionStorageKey);

  if (storedSessionId) {
    return storedSessionId;
  }

  const sessionId = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  sessionStorage.setItem(sessionStorageKey, sessionId);
  return sessionId;
}
